import { isRulebookEnabled } from '../utils/rulebookUtils';
/**
 * Service for managing shop items and equipment selection
 * Handles filtering by rulebooks and categories
 */

import shopItemsData from '../data/items.json';
import rulebookSettingsMapping from '../data/rulebook-settings-mapping.json';
import rulebookShortnames from '../data/rulebook-shortnames.json';
import { rulebookService } from './RulebookService';
import { AmmunitionService } from './AmmunitionService';

export interface WeaponStats {
  range?: string;
  rateOfFire?: {
    single: string | null;
    semiAuto: string | null;
    fullAuto: string | null;
    display: string;
  };
  damage?: string;
  damageType?: string;
  penetration?: number;
  clip?: number;
  reload?: string;
  /**
   * Weapon qualities. items.json carries BOTH shapes - 478 entries use an array
   * and 268 a comma-separated string - so consumers must handle both. Declaring
   * this as `string` alone forced callers into `as ShopItem & {...}` casts.
   */
  special?: string | string[];
}

/**
 * Shop item interface representing items from items.json
 *
 * All items use string IDs as their primary identifier (e.g., "laspistol", "flak-armor").
 * No numeric itemId fields exist - the system uses string IDs exclusively.
 *
 * Requirements: 7.1, 7.2, 7.3 - String ID usage for all items
 */
export interface ShopItem {
  /** String ID from items.json (e.g., "laspistol", "hand-cannon") - primary identifier */
  id: string;
  /** When true, an NPC/xeno weapon hidden from the player shop (GM bestiary only). */
  npcOnly?: boolean;
  /** Display name of the item */
  name: string;
  /** Item type: "weapon", "armor", "gear", etc. */
  type: string;
  /** Item subtype/category for filtering (e.g., "Las", "Bolt", "Clothing & Personal Items") */
  subtype?: string;
  /** Weight in kilograms */
  weight: number;
  /** Availability rating (e.g., "Common", "Rare", "Very Rare") */
  availability: string;
  /** Cost in Thrones, null if priceless or varies */
  cost: number | null;
  /** Item description */
  description: string;
  /** Source rulebook ID (e.g., "rogue-trader-core") */
  rulebook: string;
  /** Sub-section within a rulebook (e.g., "Hive World", "ITS Ork"). Only present for multi-section rulebooks. */
  section?: string;
  /** Whether the character is proficient with this item */
  proficient?: boolean;
  /** Required proficiency to use effectively */
  proficiencyRequired?: string;
  /** Weapon class for weapons (e.g., "pistol", "basic", "melee") */
  weaponClass?: string;
  /** Weapon statistics for weapon items */
  weaponStats?: WeaponStats;
  /** Original description before any modifications */
  originalDescription?: string;
  /** Craftsmanship tier applied to the item ("Poor", "Good", "Best"...) */
  craftsmanship?: string;
  /** True when the item is a weapon upgrade rather than a weapon */
  weaponModification?: boolean;
  /** True when the item is an armour upgrade rather than armour */
  armorModification?: boolean;
  /** Armour statistics for armour items, as stored in items.json */
  armorData?: {
    locations?: string[];
    armorPoints?: number;
    special?: string[];
  };
}

export interface ShopCategory {
  id: string;
  name: string;
  items: ShopItem[];
}

export interface CategoryHierarchy {
  id: string;
  name: string;
  count: number;
  children?: CategoryHierarchy[];
  shortName?: string;
  publisher?: string;
  year?: number;
  description?: string;
}

export interface ShopFilters {
  categories?: string[];
  subtypes?: string[];
  availability?: string[];
  settings?: string[];
  rulebooks?: string[];
  excludedRulebooks?: string[];
  search?: string;
  maxCost?: number;
  maxWeight?: number;
  proficientOnly?: boolean;
}

export interface RulebookInfo {
  rulebookId: string;
  fullName: string;
  shortName: string;
  publisher: string;
  year: number;
  description: string;
}

export class ShopService {
  private static instance: ShopService;
  private allItems: ShopItem[];
  /**
   * Runtime-registered items (e.g. NPC sheet weapons parsed from `weaponsText`).
   * Kept separate from `allItems` so `getItemById` resolves them while the player
   * shop, weapon filters and statistics stay driven purely by items.json.
   */
  private runtimeItems: Map<string, ShopItem> = new Map();
  private rulebookToDefaultSection: Map<string, string>;

  private constructor() {
    this.allItems = shopItemsData.items as ShopItem[];

    // Build reverse lookup: rulebookId -> default display name from mapping
    this.rulebookToDefaultSection = new Map();
    const mappings = (
      rulebookSettingsMapping as { mappings: Record<string, { rulebookId: string }> }
    ).mappings;
    for (const [displayName, mapping] of Object.entries(mappings)) {
      // Use the first mapping encountered for each rulebookId as the default
      if (!this.rulebookToDefaultSection.has(mapping.rulebookId)) {
        this.rulebookToDefaultSection.set(mapping.rulebookId, displayName);
      }
    }
  }

  /** Get the section label for an item. Falls back to a display name derived from the rulebook. */
  private getItemSection(item: ShopItem): string {
    const primaryRulebook = Array.isArray(item.rulebook) ? item.rulebook[0] : item.rulebook;
    return item.section || this.rulebookToDefaultSection.get(primaryRulebook) || primaryRulebook;
  }

  public static getInstance(): ShopService {
    if (!ShopService.instance) {
      ShopService.instance = new ShopService();
    }
    return ShopService.instance;
  }

  /**
   * Get all shop items
   */
  public getAllItems(): ShopItem[] {
    return this.allItems.filter((item) => !item.npcOnly);
  }

  /** All items including NPC-only ones (GM tools). Bypasses the npcOnly + rulebook filters. */
  public getAllItemsIncludingNpcOnly(): ShopItem[] {
    return [...this.allItems];
  }

  /**
   * Get items filtered by enabled rulebooks
   */
  public getAvailableItems(): ShopItem[] {
    const enabledRulebooks = rulebookService.getEnabledRulebookIds();
    return this.allItems.filter((item) => {
      // NPC-only items never appear in the player shop; then filter by enabled rulebook.
      return !item.npcOnly && isRulebookEnabled(item.rulebook, enabledRulebooks);
    });
  }

  /**
   * Get items by category (subtype is the real category)
   */
  public getItemsByCategory(category: string, availableOnly: boolean = true): ShopItem[] {
    const items = availableOnly ? this.getAvailableItems() : this.getAllItems();
    return items.filter((item) => item.subtype === category);
  }

  /**
   * Get items by section (sub-category within a rulebook)
   */
  public getItemsBySetting(setting: string, availableOnly: boolean = true): ShopItem[] {
    const items = availableOnly ? this.getAvailableItems() : this.getAllItems();
    return items.filter((item) => this.getItemSection(item) === setting);
  }

  /**
   * Get all available categories (based on subtypes)
   */
  public getCategories(availableOnly: boolean = true): ShopCategory[] {
    const items = availableOnly ? this.getAvailableItems() : this.getAllItems();
    const categoryMap = new Map<string, ShopItem[]>();

    items.forEach((item) => {
      const category = item.subtype || 'Uncategorized';
      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }
      categoryMap.get(category)!.push(item);
    });

    return Array.from(categoryMap.entries())
      .map(([subtype, items]) => ({
        id: subtype,
        name: this.getCategoryDisplayName(subtype),
        items: items.sort((a, b) => a.name.localeCompare(b.name))
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Get hierarchical category structure for filtering
   */
  public getHierarchicalCategories(availableOnly: boolean = true): CategoryHierarchy[] {
    const items = availableOnly ? this.getAvailableItems() : this.getAllItems();
    const categoryMap = new Map<string, number>();
    const weaponCategoryMap = new Map<string, number>();
    const armorCategoryMap = new Map<string, number>();

    // Count items by subtype, and separately by type:subtype for weapons/armor
    items.forEach((item) => {
      const category = item.subtype || 'Uncategorized';
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1);

      // Also count by type for disambiguation (e.g., Primitive weapons vs Primitive armor)
      if (item.type === 'weapon') {
        weaponCategoryMap.set(category, (weaponCategoryMap.get(category) || 0) + 1);
      } else if (item.type === 'armor') {
        armorCategoryMap.set(category, (armorCategoryMap.get(category) || 0) + 1);
      }
    });

    // Count ammunition items (items whose ID matches an AmmunitionDefinition)
    const ammoIds = new Set(AmmunitionService.getAllDefinitions().map((d) => d.id));
    const ammoCount = items.filter((item) => ammoIds.has(item.id)).length;

    // Define the hierarchical structure
    const hierarchy: CategoryHierarchy[] = [
      {
        id: 'weapons',
        name: 'Weapons',
        count: 0,
        children: [
          {
            id: 'ranged-weapons',
            name: 'Ranged Weapons',
            count: 0,
            children: [
              {
                id: 'Las',
                name: 'Las Weapons',
                count: categoryMap.get('Las') || 0
              },
              {
                id: 'Bolt',
                name: 'Bolt Weapons',
                count: categoryMap.get('Bolt') || 0
              },
              {
                id: 'Plasma',
                name: 'Plasma Weapons',
                count: categoryMap.get('Plasma') || 0
              },
              {
                id: 'Melta',
                name: 'Melta Weapons',
                count: categoryMap.get('Melta') || 0
              },
              {
                id: 'Flame',
                name: 'Flame Weapons',
                count: categoryMap.get('Flame') || 0
              },
              {
                id: 'SP',
                name: 'Solid Projectile',
                count: categoryMap.get('SP') || 0
              },
              {
                id: 'Shotgun',
                name: 'Shotgun',
                count: categoryMap.get('Shotgun') || 0
              },
              {
                id: 'Needle',
                name: 'Needle Weapons',
                count: categoryMap.get('Needle') || 0
              },
              {
                id: 'Shock',
                name: 'Shock Weapons',
                count: categoryMap.get('Shock') || 0
              },
              {
                id: 'Launcher',
                name: 'Launchers',
                count: categoryMap.get('Launcher') || 0
              },
              {
                id: 'Exotic',
                name: 'Exotic Weapons',
                count: categoryMap.get('Exotic') || 0
              },
              {
                id: 'Pistol',
                name: 'Pistols',
                count: categoryMap.get('Pistol') || 0
              }
            ]
          },
          {
            id: 'melee-weapons',
            name: 'Melee Weapons',
            count: 0,
            children: [
              {
                id: 'Melee',
                name: 'Basic Melee',
                count: categoryMap.get('Melee') || 0
              },
              {
                id: 'Chain',
                name: 'Chain Weapons',
                count: categoryMap.get('Chain') || 0
              },
              {
                id: 'Power',
                name: 'Power Weapons',
                count: categoryMap.get('Power') || 0
              },
              {
                id: 'weapon:Primitive',
                name: 'Primitive Weapons',
                count: weaponCategoryMap.get('Primitive') || 0
              }
            ]
          },
          {
            id: 'weapon-accessories',
            name: 'Weapon Accessories',
            count: 0,
            children: [
              {
                id: 'Sight',
                name: 'Weapon Sights',
                count: categoryMap.get('Sight') || 0
              },
              {
                id: 'Grenades & Explosives',
                name: 'Grenades & Explosives',
                count: categoryMap.get('Grenades & Explosives') || 0
              }
            ]
          },
          {
            id: 'ammunition',
            name: 'Ammunition',
            count: ammoCount
          }
        ]
      },
      {
        id: 'armor',
        name: 'Armor & Protection',
        count: 0,
        children: [
          {
            id: 'light-armor',
            name: 'Light Armor',
            count: 0,
            children: [
              {
                id: 'Basic',
                name: 'Basic Armor',
                count: categoryMap.get('Basic') || 0
              },
              {
                id: 'armor:Primitive',
                name: 'Primitive Armor',
                count: armorCategoryMap.get('Primitive') || 0
              },
              {
                id: 'Mesh Armour',
                name: 'Mesh Armor',
                count: categoryMap.get('Mesh Armour') || 0
              }
            ]
          },
          {
            id: 'medium-armor',
            name: 'Medium Armor',
            count: 0,
            children: [
              {
                id: 'Flak Armour',
                name: 'Flak Armor',
                count: categoryMap.get('Flak Armour') || 0
              },
              {
                id: 'Carapace Armour',
                name: 'Carapace Armor',
                count: categoryMap.get('Carapace Armour') || 0
              },
              {
                id: 'Other Armour',
                name: 'Other Armor',
                count: categoryMap.get('Other Armour') || 0
              }
            ]
          },
          {
            id: 'heavy-armor',
            name: 'Heavy Armor',
            count: 0,
            children: [
              {
                id: 'Power Armour',
                name: 'Power Armor',
                count: categoryMap.get('Power Armour') || 0
              }
            ]
          },
          {
            id: 'Force Field',
            name: 'Force Fields',
            count: categoryMap.get('Force Field') || 0
          }
        ]
      },
      {
        id: 'cybernetics',
        name: 'Cybernetics',
        count: 0,
        children: [
          {
            id: 'Implant',
            name: 'Implants',
            count: categoryMap.get('Implant') || 0
          },
          {
            id: 'Bionic Limb',
            name: 'Bionic Limbs',
            count: categoryMap.get('Bionic Limb') || 0
          },
          {
            id: 'Bionic Organ',
            name: 'Bionic Organs',
            count: categoryMap.get('Bionic Organ') || 0
          },
          {
            id: 'Mechadendrite',
            name: 'Mechadendrites',
            count: categoryMap.get('Mechadendrite') || 0
          }
        ]
      },
      {
        id: 'gear',
        name: 'Equipment & Gear',
        count: 0,
        children: [
          {
            id: 'Clothing & Personal Items',
            name: 'Clothing & Personal Items',
            count: categoryMap.get('Clothing & Personal Items') || 0
          },
          {
            id: 'Tools',
            name: 'Tools',
            count: categoryMap.get('Tools') || 0
          },
          {
            id: 'Drugs & Consumables',
            name: 'Drugs & Consumables',
            count: categoryMap.get('Drugs & Consumables') || 0
          }
        ]
      },
      {
        id: 'uncategorized',
        name: 'Uncategorized',
        count: categoryMap.get('') || 0
      }
    ];

    // Calculate parent counts and filter out empty categories
    const calculateCounts = (categories: CategoryHierarchy[]): CategoryHierarchy[] => {
      return categories
        .map((category) => {
          if (category.children) {
            const filteredChildren = calculateCounts(category.children).filter(
              (child) => child.count > 0
            );
            const totalCount = filteredChildren.reduce((sum, child) => sum + child.count, 0);

            return {
              ...category,
              count: totalCount,
              children: filteredChildren.length > 0 ? filteredChildren : undefined
            };
          }
          return category;
        })
        .filter((category) => category.count > 0);
    };

    return calculateCounts(hierarchy);
  }

  /**
   * Get all available settings
   */
  public getSettings(availableOnly: boolean = true): string[] {
    const items = availableOnly ? this.getAvailableItems() : this.getAllItems();
    const settings = new Set<string>();

    items.forEach((item) => {
      const section = this.getItemSection(item);
      if (section) {
        settings.add(section);
      }
    });

    return Array.from(settings).sort();
  }

  /**
   * Look up a rulebook entry by ID across all systems.
   * Returns the entry or undefined if not found.
   */
  private getRulebookEntry(
    rulebookId: string
  ): { shortName: string; fullName: string } | undefined {
    for (const system of Object.values(rulebookShortnames.systems)) {
      const rulebooks = system.rulebooks as Record<string, { shortName: string; fullName: string }>;
      if (rulebooks[rulebookId]) {
        return rulebooks[rulebookId];
      }
    }
    return undefined;
  }

  /**
   * Get rulebook shortName from rulebook ID
   */
  public getRulebookShortName(rulebookId: string | string[]): string {
    const id = Array.isArray(rulebookId) ? rulebookId[0] : rulebookId;
    return this.getRulebookEntry(id)?.shortName ?? id;
  }

  /**
   * Get rulebook fullName from rulebook ID
   */
  public getRulebookFullName(rulebookId: string | string[]): string {
    const id = Array.isArray(rulebookId) ? rulebookId[0] : rulebookId;
    return this.getRulebookEntry(id)?.fullName ?? id;
  }

  /**
   * Get all rulebooks grouped by system (from complete reference, not just shop items)
   */
  public getRulebooksBySystem(): Record<string, { name: string; rulebooks: string[] }> {
    const grouped: Record<string, { name: string; rulebooks: string[] }> = {};

    for (const [systemKey, system] of Object.entries(rulebookShortnames.systems)) {
      const systemRulebooks = system.rulebooks as Record<
        string,
        { shortName: string; fullName: string }
      >;
      grouped[systemKey] = {
        name: system.name,
        rulebooks: Object.keys(systemRulebooks)
      };
    }

    return grouped;
  }

  /**
   * Get display name for a category (subtype)
   */
  private getCategoryDisplayName(category: string): string {
    // For subtypes, just return as-is since they're already descriptive
    if (!category || category === '') {
      return 'Uncategorized';
    }
    return category;
  }

  /**
   * Search items with filters
   */
  public searchItems(filters: ShopFilters = {}): ShopItem[] {
    let items = this.getAllItems();

    // Filter by rulebooks (if not specified, use available items based on enabled rulebooks)
    if (filters.rulebooks && filters.rulebooks.length > 0) {
      // Custom filter: use specified rulebooks
      items = items.filter((item) => filters.rulebooks!.includes(item.rulebook));
    } else {
      // Default: filter by item.rulebook against enabled rulebooks
      const enabledRulebooks = rulebookService.getEnabledRulebookIds();
      items = items.filter((item) => isRulebookEnabled(item.rulebook, enabledRulebooks));
    }

    // Exclude specific rulebooks (applied after include filter)
    if (filters.excludedRulebooks && filters.excludedRulebooks.length > 0) {
      items = items.filter((item) => !filters.excludedRulebooks!.includes(item.rulebook));
    }

    // Filter by categories (subtypes) - supports compound IDs like "weapon:Primitive"
    if (filters.categories && filters.categories.length > 0) {
      const ammoIds = filters.categories.includes('ammunition')
        ? new Set(AmmunitionService.getAllDefinitions().map((d) => d.id))
        : null;
      items = items.filter((item) => {
        const itemSubtype = item.subtype || 'Uncategorized';
        return filters.categories!.some((category) => {
          // Special handling for ammunition virtual category
          if (category === 'ammunition') {
            return ammoIds!.has(item.id);
          }
          // Check for compound ID format "type:subtype"
          if (category.includes(':')) {
            const [type, subtype] = category.split(':');
            return item.type === type && itemSubtype === subtype;
          }
          // Simple subtype match
          return itemSubtype === category;
        });
      });
    }

    // Filter by subtypes (alias for categories)
    if (filters.subtypes && filters.subtypes.length > 0) {
      items = items.filter((item) => filters.subtypes!.includes(item.subtype || 'Uncategorized'));
    }

    // Filter by settings (sections)
    if (filters.settings && filters.settings.length > 0) {
      items = items.filter((item) => filters.settings!.includes(this.getItemSection(item)));
    }

    // Filter by availability
    if (filters.availability && filters.availability.length > 0) {
      items = items.filter((item) => filters.availability!.includes(item.availability));
    }

    // Filter by search term
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      items = items.filter(
        (item) =>
          item.name.toLowerCase().includes(searchTerm) ||
          item.description.toLowerCase().includes(searchTerm) ||
          (item.subtype && item.subtype.toLowerCase().includes(searchTerm))
      );
    }

    // Filter by max cost
    if (filters.maxCost !== undefined) {
      items = items.filter((item) => item.cost === null || item.cost <= filters.maxCost!);
    }

    // Filter by max weight
    if (filters.maxWeight !== undefined) {
      items = items.filter((item) => item.weight <= filters.maxWeight!);
    }

    return items.sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Get item by string ID
   *
   * Looks up an item from items.json using its string identifier.
   * All item references in the system use string IDs (e.g., "laspistol", "flak-armor").
   *
   * @param id - The string ID of the item (e.g., "laspistol", "hand-cannon")
   * @returns The ShopItem if found, undefined otherwise
   *
   * Requirements: 7.1, 7.3 - All item references use string IDs
   */
  public getItemById(id: string): ShopItem | undefined {
    return this.allItems.find((item) => item.id === id) ?? this.runtimeItems.get(id);
  }

  /**
   * Register a runtime-only item so it resolves via `getItemById` (used for NPC
   * sheet weapons parsed at run time). Idempotent by id; does not appear in the
   * player shop, weapon filters, or statistics.
   */
  public registerRuntimeItem(item: ShopItem): void {
    this.runtimeItems.set(item.id, item);
  }

  /**
   * Check if an item is available (from enabled rulebooks)
   *
   * @param itemId - The string ID of the item to check
   * @returns true if the item exists and its rulebook is enabled
   *
   * Requirements: 7.1, 7.3 - All item references use string IDs
   */
  public isItemAvailable(itemId: string): boolean {
    const item = this.getItemById(itemId);
    if (!item) return false;

    const enabledRulebooks = rulebookService.getEnabledRulebookIds();
    return isRulebookEnabled(item.rulebook, enabledRulebooks);
  }

  /**
   * Get availability levels
   */
  public getAvailabilityLevels(): string[] {
    return [
      'Plentiful',
      'Abundant',
      'Common',
      'Average',
      'Uncommon',
      'Scarce',
      'Rare',
      'Very Rare',
      'Extremely Rare',
      'Near Unique',
      'Unique'
    ];
  }

  /**
   * Get statistics about the shop items
   */
  public getStatistics(): {
    totalItems: number;
    availableItems: number;
    unavailableItems: number;
    categoryCounts: Record<string, number>;
    rulebookCounts: Record<string, number>;
    availabilityCounts: Record<string, number>;
    settingCounts: Record<string, number>;
  } {
    const availableItems = this.getAvailableItems();
    const enabledRulebooks = rulebookService.getEnabledRulebookIds();
    const unavailableItems = this.allItems.filter(
      (item) => !isRulebookEnabled(item.rulebook, enabledRulebooks)
    );

    const categoryCounts: Record<string, number> = {};
    const rulebookCounts: Record<string, number> = {};
    const availabilityCounts: Record<string, number> = {};
    const settingCounts: Record<string, number> = {};

    this.allItems.forEach((item) => {
      const category = item.subtype || 'Uncategorized';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
      const rbs = Array.isArray(item.rulebook) ? item.rulebook : [item.rulebook];
      rbs.forEach((rb) => {
        rulebookCounts[rb] = (rulebookCounts[rb] || 0) + 1;
      });
      availabilityCounts[item.availability] = (availabilityCounts[item.availability] || 0) + 1;
      settingCounts[this.getItemSection(item)] =
        (settingCounts[this.getItemSection(item)] || 0) + 1;
    });

    return {
      totalItems: this.allItems.length,
      availableItems: availableItems.length,
      unavailableItems: unavailableItems.length,
      categoryCounts,
      rulebookCounts,
      availabilityCounts,
      settingCounts
    };
  }
}

// Export singleton instance
export const shopService = ShopService.getInstance();
